/**
 * The people who live on the archipelago.
 *
 * Two of them so far, and they are two real people: Marko, who runs Talas Akademija, and Mario.
 * Drawn from photographs at sixteen pixels wide, which is a constraint worth stating plainly —
 * at this size a face is about four decisions. Hair colour and volume, skin, one distinguishing
 * feature, shirt. Everything past that is noise the eye cannot resolve, and getting those four
 * right is the whole difference between "that's Marko" and "that's a guy".
 *
 * So: Marko is the dark-haired one with the teal streak in his fringe, the moustache and goatee,
 * in the dark red shirt with the small gold mark on it. Mario is the pale one with the platinum
 * hair swept up off his forehead and blue eyes, in a cream shirt. Side by side on a beach, nobody
 * who knows them has to be told which is which — which is the only test that matters.
 *
 * They are composed the same way the player is: the shared body art from sprites.ts, plus a patch
 * of rows per view. That is not a saving, it is a guarantee — a character can never drift out of
 * proportion with the hero standing next to them, because they are the same fourteen rows.
 */

import type { CharacterId, FacingId } from '../../core/content';
import { HAIR, type Patch } from './features';
import { palette } from './palette';
import { poseRows, spriteColors, viewOf, type BodyView, type Facing, type SpriteColors } from './sprites';

export type { Patch };

/**
 * The crop everybody here is drawn on top of.
 *
 * The base body has no hair on it any more — see `sprites.ts`. Marko's patch was authored against a
 * head that already had the short crop on it and only redraws the rows it changes, so the crop has
 * to go on first or he arrives with a shaved head and a fringe floating over it. Mario replaces
 * rows 1-5 outright and would survive either way; he gets it too, because "every character is the
 * default head plus a patch" is a rule worth being able to state without an exception in it.
 */
const CROP = HAIR[0]!;

export interface CharacterArt {
  /** What the dialogue box calls them, unless the content overrides it. */
  name: string;
  colors: SpriteColors;
  overlay: Record<BodyView, Patch>;
}

/* ------------------------------------------------------------------ extra ink */

/**
 * Four colour keys the hero never needed.
 *
 * `L` a hair highlight — platinum reads as grey without one. `j` a dyed streak. `q` facial hair,
 * which has to be its own key rather than reusing the hair colour: a moustache the same value as
 * the hair above it merges with the head at this size. `i` an iris, and `v` a mark on a shirt.
 */
const MARIO: CharacterArt = {
  name: 'Mario',
  colors: {
    ...spriteColors,
    s: '#f7dcc4',
    S: '#e2b294',
    h: '#e2d6ac',
    H: '#bcae82',
    L: '#f8f2da',
    i: '#3f79b0',
    c: '#f1ecdf',
    C: '#cdc6b4',
    p: '#3a4f6b',
    P: '#243449',
    b: '#6d5233',
    B: '#42301c',
  },
  overlay: {
    // Crown widened by a pixel each side and lit from the top left, then a fringe swept across the
    // forehead from the left. The eyes are the only other thing that changes, and they are blue.
    down: {
      1: '....kkkkkkkk....',
      2: '..kkhLLLLhhhkk..',
      3: '..khLLLhhhhhhk..',
      4: '..khhhhsssssHk..',
      5: '..khssississHk..',
    },
    up: {
      1: '....kkkkkkkk....',
      2: '..kkhLLLLhhhkk..',
      3: '..khLLLhhhhhhk..',
    },
    side: {
      1: '....kkkkkkk.....',
      2: '...khhhhhhhk....',
      3: '...khLLhhhhhk...',
      5: '...khhhsissk....',
    },
  },
};

const MARKO: CharacterArt = {
  name: 'Marko',
  colors: {
    ...spriteColors,
    s: '#e8b98c',
    S: '#c9946a',
    // Dark brown, not black. Against the black outline every character already carries, true black
    // hair turns the whole head into one silhouette with two eyes punched in it.
    h: '#3a2b1f',
    H: '#241a12',
    j: '#2f93ad',
    // A shade between the hair and the outline. Facial hair the same value as the outline reads as
    // a hole in the face; the same value as the hair reads as a hood.
    q: '#2e2118',
    c: '#8f2733',
    C: '#68171f',
    v: '#f0c243',
    p: '#2c2f3a',
    P: '#1a1c24',
    b: '#5a4a3a',
    B: '#382c22',
  },
  overlay: {
    // A fringe down to the eyebrows with the streak in it, a moustache across the mouth row, and a
    // goatee two pixels wide — the width is the whole difference between a goatee and a beard at
    // this size. The gold mark sits low on the shirt, where the embroidery is.
    down: {
      4: '..khhjjhhhhhhk..',
      7: '...khqqqqqqhk...',
      8: '....kssqqssk....',
      12: '..skcccccvvcCs..',
    },
    up: {
      4: '..kjjhhhhhhhHk..',
    },
    side: {
      4: '...khhjjssskk...',
      7: '....khhqqqqk....',
      8: '.....kssqqk.....',
      12: '...skcccvvcC....',
    },
  },
};

/**
 * Every character the content may reference. The Record type is the point: adding an id to
 * CHARACTER_IDS without drawing them is a compile error, not a person-shaped hole on a beach.
 */
export const CHARACTERS: Record<CharacterId, CharacterArt> = {
  marko: MARKO,
  mario: MARIO,
};

export const characterArt = (id: string): CharacterArt | undefined =>
  (CHARACTERS as Record<string, CharacterArt>)[id];

/** The name shown over their head: the content's override, else the drawing's own. */
export function characterName(id: string, override: string | null | undefined): string {
  return override?.trim() || characterArt(id)?.name || id;
}

/* ------------------------------------------------------------------ facing */

const FACING_OF: Record<FacingId, Facing> = {
  gore: 'up',
  dole: 'down',
  levo: 'left',
  desno: 'right',
};

export const facingOf = (id: FacingId | undefined): Facing => FACING_OF[id ?? 'dole'] ?? 'down';

/** Serbian labels for the four directions, for the builder's facing picker. */
export const FACING_LABELS: Record<FacingId, string> = {
  dole: 'dole',
  gore: 'gore',
  levo: 'levo',
  desno: 'desno',
};

/* ------------------------------------------------------------------ the pose */

/**
 * A standing character, ready to bake.
 *
 * They only ever stand. A character who walks would be a character the student can miss, and the
 * whole reason they are on the map is that they are exactly where somebody put them.
 */
export function characterPose(
  artId: string,
  facing: FacingId,
): { key: string; rows: string[]; flip: boolean; colors: SpriteColors } | null {
  const art = characterArt(artId);
  if (!art) return null;
  const direction = facingOf(facing);
  const view = viewOf(direction);
  const key = `lik:${artId}:${view}`;
  return {
    key,
    rows: poseRows(view, 'stand', [CROP[view], art.overlay[view]], key),
    flip: direction === 'left',
    colors: art.colors,
  };
}

/**
 * The little bubble over somebody who has something to say, and the reason a character reads as
 * interactive rather than as scenery. Two frames: it lifts a pixel on a slow clock.
 */
export const SPEECH_BUBBLE: readonly string[] = [
  '................',
  '....kkkkkkkk....',
  '...kddddddddk...',
  '...kdkdkdkddk...',
  '...kddddddddk...',
  '....kkkkkkkk....',
  '.....kdk........',
  '......k.........',
];

export const bubbleColors: SpriteColors = {
  k: palette.black,
  d: palette.paper,
};
